import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { keyed } from 'lit/directives/keyed.js';
import { PopBaseComponent } from '../../base/pop-base-component';
import { Constants } from './product-center-filters.constants';
import { IconSVG } from '../../common/utils/icon.js';

import style from './product-center-filters.scss?inline';
import { ChipCategory } from './filter-chips/chip/chip.model';
import productCenterStore, {
  zustandStateToComponent,
} from '../store/product-center.store';
import {
  Product,
  View,
} from '../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants as ProductCenterTableWrapperConstants } from '../product-center-table-wrapper/product-center-table-wrapper.constants';
import {
  FilterCategoryField,
  type IFilters,
  type IProductCenterStore,
} from '../store/product-center.store.model';
import { updateUrl } from '../product-center-table-wrapper/product-center-table-wrapper.utils';

/**
 * @tag pop-pc-filters-component
 * @property {tableContent} [componentObject] content from the table
 * @attribute {tableContent} [data-component-object] content from the table
 */

export class PopFiltersComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @state()
  productCenterState: IProductCenterStore = productCenterStore.getState();

  _filteredProducts: Product[] = [];

  initialRender = true; //check if this is the first render

  /**
   * @internal
   */
  @property()
  private set filteredProducts(newProducts: Product[]) {
    // Removes the stale options when filteredProducts is updated
    this.shadowRoot
      ?.querySelectorAll(`[data-cache-version="${this.cacheVersion}"]`)
      .forEach((oldOption) => {
        oldOption.remove();
      });
    // Increments cache-version needed for correct reactive rerendering of the options
    this.cacheVersion += 1;
    this._filteredProducts = newProducts;
  }

  get filteredProducts() {
    return this._filteredProducts;
  }

  /**
   * @internal
   */
  @property()
  private readonly filters!: IFilters;

  /**
   * @internal
   */
  @property()
  selectedView?: View;

  private eventHandled = false;

  // This property allows you to keep track of which multi-select options are current and which are stale.
  cacheVersion = 0;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  update(changedProperties: Map<string, unknown>) {
    super.update(changedProperties);
  }

  firstUpdated() {
    //check if URL has params and set filters to selected
    if (window.location.search.length > 0) {
      // clear out stored filters
      this.productCenterState.resetFilters();

      // get the URL params for new filter selection
      const urlParams = new URLSearchParams(window.location.search);
      const filteredParams = new URLSearchParams();
      Object.values(FilterCategoryField).forEach((category) => {
        urlParams.forEach((value, key) => {
          if (category === key) {
            filteredParams.append(key, value);
          }
        });
      });
      this.productCenterState.setFilteredProductsFromURLParams(filteredParams);
    }
  }

  private filterProducts(
    event: CustomEvent,
    filterByFiled: FilterCategoryField,
  ) {
    if (this.eventHandled) {
      return;
    }
    this.eventHandled = true;
    const { detail } = event;

    this.productCenterState.setFilteredProducts({
      categoryField: filterByFiled,
      filterValue: detail.value,
    });

    updateUrl(this.filters, this.selectedView ?? View.TABLE);
    this.requestUpdate();

    setTimeout(() => {
      this.eventHandled = false;
    }, 0);
  }

  private resetFilterHandler() {
    this.productCenterState.resetFilters();
    updateUrl(this.filters, this.selectedView ?? View.TABLE);
  }

  private renderFilter(
    categoryId: ChipCategory,
    elementId: FilterCategoryField,
  ) {
    const categoryConfig = {
      [ChipCategory.PRODUCT_RANGE]: {
        label: this.localize(Constants.TRANSLATION_KEYS.PRODUCT_RANGE),
        filterField: FilterCategoryField.PRODUCT_RANGE,
      },
      [ChipCategory.ASSET_CLASS]: {
        label: this.localize(Constants.TRANSLATION_KEYS.ASSET_CLASS),
        filterField: FilterCategoryField.ASSET_CLASS,
      },
      [ChipCategory.GEOGRAPHICAL_EXPOSURE]: {
        label: this.localize(Constants.TRANSLATION_KEYS.GEOGRAPHICAL_EXPOSURE),
        filterField: FilterCategoryField.GEOGRAPHICAL_EXPOSURE,
      },
      [ChipCategory.DISTRIBUTION_FREQUENCY]: {
        label: this.localize(Constants.TRANSLATION_KEYS.DISTRIBUTION_FREQUENCY),
        filterField: FilterCategoryField.DISTRIBUTION_FREQUENCY,
      },
    };
    const { label, filterField } =
      categoryConfig[categoryId] || categoryConfig[ChipCategory.PRODUCT_RANGE];
    const filterCategory = this.filters[filterField] || [];
    const categories = filterCategory;
    return keyed(
      `${this.cacheVersion}`,
      html`
        <beacon-multi-select
          id=${elementId}
          placeholder="${this.localize(Constants.TRANSLATION_KEYS.SELECT)}"
          @beacon-event-dismiss=${() => {
            this.productCenterState.resetFilters(filterField);
            updateUrl(this.filters, this.selectedView ?? View.TABLE);
          }}
          @beacon-event-toggle=${(event: CustomEvent) =>
            this.filterProducts(event, filterField)}
        >
          <span slot="label" class="select-label">${label}</span>
          ${keyed(
            `${elementId}${this.cacheVersion}`,
            html`<beacon-multi-select-optgroup
              data-cache-version="${this.cacheVersion}"
            >
              ${map(categories, ({ name, count, isSelected }) =>
                keyed(
                  `${name}${this.cacheVersion}${isSelected}`,
                  html`
                    <beacon-multi-select-option
                      ?selected=${isSelected}
                      id="${name}"
                      data-cache-version="${this.cacheVersion}"
                    >
                      ${name}
                      <span slot="count">${count}</span>
                    </beacon-multi-select-option>
                  `,
                ),
              )}
            </beacon-multi-select-optgroup>`,
          )}
        </beacon-multi-select>
      `,
    );
  }

  renderComponent() {
    const hasAtLeastOneFilter = (filters: IFilters): boolean => {
      return Object.values(filters).some(
        (filterCategory) =>
          filterCategory && filterCategory.some((cat) => cat.isSelected),
      );
    };
    return html`
      <div class="filters">
        <div class="container-labels">
          <div class="filters-label">
            ${this.localize(Constants.TRANSLATION_KEYS.FILTERS)}
          </div>
          <beacon-button
            @beacon-event-click=${this.resetFilterHandler}
            class="reset-filter"
            ?disabled=${!hasAtLeastOneFilter(this.filters)}
            icon-align="left"
            variant="tertiary"
            scale="normal"
          >
            ${IconSVG.circularClose(
              !hasAtLeastOneFilter(this.filters)
                ? Constants.ICON_TERTIARY_INACTIVE_COLOR
                : undefined,
              {
                slot: ProductCenterTableWrapperConstants.PREFIX_SLOT,
                customClass:
                  ProductCenterTableWrapperConstants.CHEVRON_CUSTOM_CLASS,
              },
            )}
            ${this.localize(Constants.TRANSLATION_KEYS.RESET_FILTERS)}
          </beacon-button>
        </div>
        <div class="filters-container">
          <div
            class="define-exposure b-typography-sans-serif--body--200 b-font-weight-light"
          >
            ${this.localize(Constants.TRANSLATION_KEYS.DEFINE_YOUR_EXPOSURE)}
          </div>
          <div class="filter-dropdowns">
            ${this.renderFilter(
              ChipCategory.PRODUCT_RANGE,
              FilterCategoryField.PRODUCT_RANGE,
            )}
            ${this.renderFilter(
              ChipCategory.ASSET_CLASS,
              FilterCategoryField.ASSET_CLASS,
            )}
            ${this.renderFilter(
              ChipCategory.GEOGRAPHICAL_EXPOSURE,
              FilterCategoryField.GEOGRAPHICAL_EXPOSURE,
            )}
            ${this.renderFilter(
              ChipCategory.DISTRIBUTION_FREQUENCY,
              FilterCategoryField.DISTRIBUTION_FREQUENCY,
            )}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this._filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

export const productCenterFiltersMapState = (productCenterState) => {
  return {
    filteredProducts: productCenterState.getFilteredProducts(),
    filters: productCenterState.filters,
    searchTerm: productCenterState.searchTerm,
    selectedView: productCenterState.selectedView,
  };
};

if (!customElements.get(Constants.TAG_NAME)) {
  customElements.define(
    Constants.TAG_NAME,
    zustandStateToComponent(
      {
        mapState: (productCenterState) => {
          return {
            ...productCenterFiltersMapState(productCenterState),
          };
        },
      },
      PopFiltersComponent,
    ),
  );
}
